<script lang="ts" setup>
import type { ComputedRef, Ref } from "vue"
import type { ISelectItem } from "@/types"
import { autoUpdate, flip, useFloating } from "@floating-ui/vue"
import { onClickOutside, useScroll } from "@vueuse/core"
import { useField } from "vee-validate"
import { computed, nextTick, ref, watch, watchEffect } from "vue"
import { Hint, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

interface SelectProps {
  name: string
  items?: ISelectItem[]
  modelValue?: string | number | object
  closeOnSelect?: boolean
  clearable?: boolean
  placeholder?: string
  label?: string
  error?: string
  returnObject?: boolean
  hint?: string
  noBorder?: boolean
  disabled?: boolean
  search?: boolean
  isLoadingMoreItems?: boolean
  isHeader?: boolean
}

const props = withDefaults(defineProps<SelectProps>(), {
  items: () => [],
  closeOnSelect: true,
  clearable: false,
  placeholder: "",
  label: "",
  error: "",
  returnObject: false,
  hint: "",
  noBorder: false,
  disabled: false,
  search: false,
  isLoadingMoreItems: false,
  isHeader: false,
})

const emit = defineEmits(["update:modelValue", "change", "clear", "loadMore"])

const { t } = useTranslation()

const selectRef: Ref<HTMLElement | null> = ref(null)
const selectInputRef: Ref<HTMLElement | null> = ref(null)
const selectItemsRef: Ref<HTMLElement | null> = ref(null)
const searchInputRef: Ref<HTMLElement | null> = ref(null)
const searchItemRef: Ref<string> = ref("")

const filteredItems = computed(() =>
  props.items.filter(item =>
    item.label.toLowerCase().includes(searchItemRef.value.toLowerCase()),
  ),
)

const { arrivedState } = useScroll(selectItemsRef)

watchEffect(() => {
  if (arrivedState.bottom)
    emit("loadMore")
})

const {
  value: field,
  errorMessage,
  handleChange,
  handleReset,
} = useField<string | number | ISelectItem>(() => props.name)
const selectItemsVisible: Ref<boolean> = ref(false)

function toggleSelectListVisibility(): void {
  selectItemsVisible.value = !selectItemsVisible.value
  searchItemRef.value = ""
  nextTick(() => searchInputRef.value?.focus(),
  )
}

onClickOutside(selectRef, () => {
  selectItemsVisible.value = false
  searchItemRef.value = ""
})

const { floatingStyles } = useFloating(selectInputRef, selectItemsRef, {
  placement: "bottom-start",
  whileElementsMounted: autoUpdate,
  middleware: [flip({ crossAxis: false })],
})

function selectItem(item: ISelectItem) {
  if (item.disabled)
    return

  emit("update:modelValue", props.returnObject ? item : item.value)
  emit("change", props.returnObject ? item : item.value)
  handleChange(props.returnObject ? item : item.value)

  if (props.closeOnSelect)
    selectItemsVisible.value = false
}

function clearInput(): void {
  selectItemsVisible.value = false
  handleReset()
  emit("update:modelValue", props.returnObject ? { label: "", value: "" } : "")
  emit("change", props.returnObject ? { label: "", value: "" } : "")
  emit("clear")
  searchItemRef.value = ""
}

function isUnsetSelectValue(value: unknown): boolean {
  return value === undefined || value === null || value === ""
}

function labelForSelectValue(value: string | number | ISelectItem): string | number | undefined {
  if (typeof value === "object" && value !== null && "value" in value)
    return (value as ISelectItem).label
  return props.items.find((item: ISelectItem) => item.value === value)?.label ?? value
}

const computedValue = computed(() => {
  if (!isUnsetSelectValue(props.modelValue))
    return labelForSelectValue(props.modelValue as string | number | ISelectItem)
  if (!isUnsetSelectValue(field.value))
    return labelForSelectValue(field.value as string | number | ISelectItem)
  return undefined
})

const showClearable: ComputedRef<boolean> = computed(() => {
  return Boolean(props.clearable && computedValue.value)
})

watch(() => props.modelValue, (newVal) => {
  if (newVal === undefined || newVal === null || newVal === "")
    handleReset()
  else
    handleChange(newVal)
}, { immediate: true })
</script>

<template>
  <div>
    <div ref="selectRef" class="gpx-select-wrapper">
      <div
        class="gpx-input-container"
        :class="{
          'gpx-input-container_error': error || errorMessage,
          'gpx-input-container_noBorder': noBorder,
          'gpx-input_disabled': disabled,
        }"
      >
        <label class="gpx-input-label-container" :for="name">
          <input
            ref="selectInputRef"
            :name="name"
            readonly
            :style="isHeader ? 'font-size:20px;' : ''"
            class="gpx-input"
            :class="{
              'gpx-input_disabled': disabled,
              'gpx-select-input_open': selectItemsVisible,
              'gpx-select-input_no-label': !label,
              'font-comfortaa font-semibold mt-px': isHeader,
            }"
            :value="computedValue"
            :placeholder="label ? '' : placeholder"
            :disabled="disabled"
            @click="toggleSelectListVisibility"
            @keyup.space="toggleSelectListVisibility"
            @keyup.enter="toggleSelectListVisibility"
            @keyup.esc="selectItemsVisible && toggleSelectListVisibility()"
          >
          <span
            class="gpx-floating-label"
            :class="{
              'gpx-floating-label_is-floating': computedValue,
              'gpx-floating-label_error': error || errorMessage,
            }"
          >{{ label }}</span>
        </label>
        <button
          v-if="!showClearable"
          variant="flat"
          type="button"
          class="px-5"
          :class="{ 'cursor-not-allowed': disabled }"
          :disabled="disabled"
          @click="toggleSelectListVisibility"
        >
          <span class="sr-only">{{ t('clear_field', { label }) }}</span>
          <Icon
            icon="CircleChevronDown"
            class="gpx-select-icon"
            :style="`transform: rotate(${selectItemsVisible ? '180' : '0'}deg)`"
          />
        </button>
        <button
          v-if="showClearable"
          variant="flat"
          type="button"
          class="px-5"
          :class="{ 'cursor-not-allowed': disabled }"
          :disabled="disabled"
          data-test="gpx-input-clear-button"
          @click="clearInput"
        >
          <span class="sr-only">{{ t('clear_field', { label }) }}</span>
          <Icon icon="Close" size="sm" />
        </button>
      </div>
      <ul
        v-if="selectItemsVisible && items.length"
        ref="selectItemsRef"
        class="gpx-select-items z-20 rounded-sm overflow-y-auto"
        :class="{ 'gpx-select-items_open': selectItemsVisible }"
        :style="floatingStyles"
      >
        <label
          v-if="search"
          for="selectSearch"
          class="flex gap-2 p-2 w-full border-b bg-white sticky -top-1 -mt-1 z-10"
        >
          <Icon icon="Search" />
          <input
            id="selectSearch"
            ref="searchInputRef"
            v-model="searchItemRef"
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
            class="outline-hidden border-none flex-1"
            :placeholder="t('search_dot')"
            @keydown.enter.prevent
            @keyup.enter.prevent="toggleSelectListVisibility"
            @keyup.esc.prevent="selectItemsVisible && toggleSelectListVisibility()"
          >
        </label>
        <li
          v-for="(item, index) in filteredItems"
          :key="item.label"
          class="gpx-select-item"
          :class="{ 'gpx-select-item_disabled': item.disabled ?? false }"
        >
          <button
            class="gpx-select-item-button"
            :disabled="item.disabled"
            @click.prevent="() => selectItem(item)"
            @focusout="index + 1 === items.length && (selectItemsVisible = false)"
          >
            <slot name="item" :item="item">
              {{ item.label }}
            </slot>
          </button>
        </li>
        <li v-if="search && !filteredItems.length" class="p-4">
          {{ t('no_data_available') }}
        </li>
        <li v-if="isLoadingMoreItems" class="w-full flex gap-2 items-center justify-center p-2">
          <Icon icon="Loading" /> {{ t('select_input.loading_items') }}
        </li>
      </ul>
      <div v-else-if="selectItemsVisible" class="flex">
        <div class="gpx-select-items gpx-select-items_empty">
          <slot name="empty">
            <p>{{ t('no_data_available') }}</p>
          </slot>
        </div>
      </div>
    </div>
    <span v-if="error || errorMessage" class="gpx-field-error">{{ error || errorMessage }}</span>
    <Hint v-if="hint" :hint="hint" class="mt-1" />
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../../assets/styles.css"

.gpx-select-input_no-label {
  padding: 0 1rem;
}

.gpx-select-wrapper {
  position: relative;
}

.gpx-floating-label {
  pointer-events: none;
}

.gpx-select-items {
  @apply w-full bg-white border border-gray-300 overflow-y-auto max-h-80 py-1;
}

.gpx-select-items_empty {
  @apply flex py-6 px-4 justify-center items-center text-sm font-medium text-gray-700 rounded-lg;
}

.gpx-select-item-button {
  background-color: transparent;
  border: none;
  width: 100%;
  height: 100%;
  padding: 0.5rem;
  transition: background-color 0.1s ease;
  text-align: start;
}

.gpx-select-item-button:hover {
  @apply bg-gray-100 cursor-pointer;
}

.gpx-select-item-button:disabled {
  background-color: transparent;
  cursor: not-allowed;
}

.gpx-select-item_disabled {
  @apply text-gray-500 cursor-not-allowed;
}

.gpx-select-icon {
  margin-right: -0.3rem;
  transition: transform 0.2s ease;
}
</style>
